<template lang="pug">
catalog-desktop(v-if="q.platform.is.desktop")
catalog-mobile(v-if="q.platform.is.mobile")
</template>

<script>
import { deepCopy } from 'assets/helpers';
import {
    setCrumbPath,
    getFiltersFromURL,
    buildCategoryTree,
    setNestedCategories,
    getModificationFilters,
} from 'assets/helpers/catalog.js';
import { find } from 'assets/helpers/categories.js';
import { cityIn } from 'lvovich';
import { useQuasar, LoadingBar, useMeta, LocalStorage, Platform, Cookies } from 'quasar';
import { computed, onMounted, ref, onServerPrefetch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { getValid_In } from 'assets/helpers/blackListCitys';

import CatalogMobile from './CatalogMobile.vue';
import CatalogDesktop from './CatalogDesktop.vue';

export default {
    name: 'Catalog',
    components: {
        CatalogMobile,
        CatalogDesktop,
    },
    async preFetch({ store, currentRoute, previousRoute, ssrContext, redirect }) {
        LoadingBar.setDefaults({
            color: 'positive',
            size: '4px',
            position: 'top',
        });
        LoadingBar.start();

        // начало загрузки
        store.commit('catalog/SET_LOADING', true);

        const cookies = process.env.SERVER
            ? Cookies?.parseSSR(ssrContext)
            : Cookies;

        if (cookies?.get('token')) {
            try {
                await store.dispatch('advertisement/GET_HISTORY');
            } catch {
            }
        }

        if (currentRoute.params.category_id != previousRoute?.params.category_id) {
            try {

                let category = await store.dispatch('categories/GET_NCATEGORY_BY_SLUG', {
                    data: {
                        slug: currentRoute.params.category_id,
                        freeDelivery: currentRoute.query.freeDelivery || false,
                    },
                });

                store.commit('categories/SET_CURRENT_NCATEGORY', category);
            } catch (e) {
                if (e.response.status == 404) {
                    return Promise.reject({ code: 404 });
                }
            }
        }

        const oldFilters = store.getters['catalog/FILTERS_FROM_URL'];

        // получаем фильтры из URL
        const filtersFromURL = getFiltersFromURL(currentRoute.query);

        // отслеживание изменения фильтров
        const filtersChanange = JSON.stringify(JSON.parse(JSON.stringify(oldFilters))) != JSON.stringify(filtersFromURL);

        const sortChange = currentRoute.query.sort != previousRoute?.query.sort;

        // сохраняем полученные фильтры
        store.commit('catalog/SET_FILTERS_FROM_URL', filtersFromURL);

        // запрос предыдущего query запроса
        const catalogId = await store.getters['catalog/CATALOG_ID'];

        // сравнение предыдущего query запроса с текущим
        if (filtersChanange || catalogId != currentRoute.params.category_id || sortChange || previousRoute.city != currentRoute.city)
            store.commit('catalog/SET_CATALOG_SCROLL_ID', null);

        // сохраняем query нового запроса, для отслеживания его измения
        store.commit('catalog/SET_CATALOG_ID', currentRoute.params.category_id);


        const scrollId = await store.getters['catalog/SCROLL_ID'];

        let data = {
            'filter': {
                'filters': [],
            },
        };

        // вычисление применяемых фильтров
        for (const [category, filter] of Object.entries(filtersFromURL)) {
            let currentFilter = { 'key': category, 'value': filter[0] };
            data.filter.filters.push(currentFilter);
        }

        if (currentRoute.query.sort) {
            let currentSort = { 'sort': { 'sort': currentRoute.query.sort } };
            Object.assign(data, currentSort);
        }

        let noJs = store.getters['device/NOJS'];

        let limit = 20;

        const platform = process.env.SERVER ? Platform.parseSSR(ssrContext) : Platform;
        if (platform.userAgent.includes('YandexBot') || platform.userAgent.includes('GoogleBot')) {
            limit = 1000;
        }

        let result = await store.dispatch('catalog/GET_PREVIEWS_BY_NCATEGORY', {
            id: currentRoute.params.category_id,
            page: currentRoute.query.page || 1,
            scrollId: scrollId || null,
            data,
            limit,
        });

        // сохраняем товары в store
        store.commit('catalog/SET_PRODUCTS', result.products);

        // сохраняем кол-вл товаров в store
        store.commit('catalog/SET_PRODUCTS_COUNT', result.totalCount);

        store.commit('catalog/SET_CATALOG_FILTERS', result.filters?.filters);

        store.commit('catalog/SET_CATALOG_SCROLL_ID', result.scrollId);

        // окончание загрузки
        store.commit('catalog/SET_LOADING', false);

        LoadingBar.stop();
    },
    setup() {
        const store = useStore();
        const route = useRoute();
        const router = useRouter();

        const blackList = ref({
            'Маслова Пристань': {
                value: 'Масловой Пристани',
            },
            'Белые Берега': {
                value: 'Белых Берегах',
            },
        });

        const city = ref();
        const metaCity = ref('Белгороде');

        const userCityId = computed(() => store.getters['user/USER_CITY_ID']);
        const currentCategory = computed(() => store.getters['categories/CURRENT_NCATEGORY']);
        const name = computed(() => currentCategory.value?.name);
        const metaKeywords = computed(() => currentCategory.value.seo_keywords);

        const seoTitle = computed(() => currentCategory.value.seo_title?.replace('{}', metaCity.value));
        const seoDescription = computed(() => currentCategory.value?.seo_description?.replace('{}', metaCity.value));
        const canonicalLink = computed(() => {
            return `https://${process.env.APP_SERVICE_HOSTNAME}/catalog/${router.currentRoute.value.params.category_id}`;
        });


        let metaTitle = computed(() => seoTitle.value || currentCategory.value?.name + ': купить в ' + metaCity.value + ' и других городах с доставкой в аптеку или на дом | Интернет-аптека eTabl.ru (партнер Таблеточка)');
        let metaDescription = computed(() => seoDescription.value || currentCategory.value?.name.toLowerCase() + ' купить в онлайн-аптеке eTabl.ru (партнер Таблеточка). Низкие цены ✅ Доставка на дом и в аптеку в ' + metaCity.value + ' ✅ Гарантия качества ✅ Удобный поиск ☎️ 8 (800) 200-5-400');
        const meta = computed(() => store.getters['categories/CATEGORIES_META']);

        if (meta.value?.title) {
            metaTitle = meta.value.title;
        }

        if (meta.value?.description) {
            metaDescription = meta.value.description;
        }

        onServerPrefetch(async () => {
            let noJs = store.getters['device/NOJS'];
        });

        onMounted(() => {

            setTimeout(async () => {
                city.value = await store.dispatch('cityForm/GET_CITY_BY_ID', {
                    city_id: userCityId.value || LocalStorage.getItem('USER_CITY_ID'),
                });
                if (blackList.value[city.value.name]?.value) {
                    metaCity.value = blackList.value[city.value.name].value;
                } else {
                    metaCity.value = cityIn(city.value.name);
                }
            }, 1000);

            if (process.env.CLIENT) {
                // window._paq.push(['trackGoal', 3]);
            }

        });

        useMeta(() => {
            let currentRout = router.currentRoute.value.path;
            if (router.currentRoute.value.name === 'Catalog' || router.currentRoute.value.params.city !== '') {
                currentRout = `/catalog/${router.currentRoute.value.params.category_id}`;
            }
            return {
                title: metaTitle.value,
                meta: {
                    ...(router.currentRoute.value.query.page > 1 ? {
                        noIndex: { name: 'robots', content: 'noindex, nofollow' },
                    } : {}),
                    description: { name: 'description', content: metaDescription.value },
                    keywords: { name: 'keywords', content: metaKeywords.value },
                    og_locale: { property: 'og:locale', content: 'ru_RU' },
                    og_type: { property: 'og:type', content: 'website' },
                    og_site_name: { property: 'og:site_name', content: 'eTabl.ru' },
                    og_title: { property: 'og:title', content: metaTitle.value },
                    og_description: { property: 'og:description', content: metaDescription.value },
                    og_url: {
                        property: 'og:url',
                        content: `https://${process.env.APP_SERVICE_HOSTNAME}` + currentRout,
                    },
                },
                link: [
                    {
                        rel: 'canonical',
                        href: canonicalLink.value,
                    },
                ],
                script: {
                    ldJson: {
                        type: 'application/ld+json',
                        innerHTML: `{
                            "@context": "https://schema.org/",
                            "@type": "BreadcrumbList",
                            "itemListElement": [{
                                "@type": "ListItem",
                                "position": "1",
                                "item": "https://${process.env.APP_SERVICE_HOSTNAME}",
                                "name": "Главная"
                            },{
                                "@type": "ListItem",
                                "position": "2",
                                "item": "https://${process.env.APP_SERVICE_HOSTNAME}${router.currentRoute.value.fullPath}",
                                "name": "${name.value}"
                            }]
                        }`,
                    },
                },
            };
        });
        return {
            q: useQuasar(),
            city,
            cityIn,
            metaCity,
            blackList,
            router,
        };
    },
};
</script>
